import React, { useEffect, useState } from "react";
import { Maximize, Minimize } from "lucide-react";
import { toast } from "sonner";

const el = () => document.documentElement;
const activeEl = () => document.fullscreenElement || document.webkitFullscreenElement;
const hasApi = () => !!(el().requestFullscreen || el().webkitRequestFullscreen);
const allowed = () => document.fullscreenEnabled ?? document.webkitFullscreenEnabled ?? true;

const STYLES = {
  dark: "border-slate-700 bg-[#070A0E]/80 text-slate-200 hover:border-cyan-400 hover:text-cyan-200",
  light: "border-blue-200 bg-white/90 text-blue-900 hover:border-blue-500 shadow-sm",
};

export const FullscreenButton = ({ variant = "dark", className = "" }) => {
  const [on, setOn] = useState(() => !!activeEl());

  useEffect(() => {
    const sync = () => setOn(!!activeEl());
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  if (!hasApi()) return null;

  const toggle = () => {
    if (activeEl()) {
      Promise.resolve((document.exitFullscreen || document.webkitExitFullscreen).call(document)).catch(() => {});
      return;
    }
    if (!allowed()) {
      window.open(window.location.href, "_blank", "noopener");
      toast("Opened in a new tab", { description: "Press the full screen button there to go full screen." });
      return;
    }
    Promise.resolve((el().requestFullscreen || el().webkitRequestFullscreen).call(el())).catch(() =>
      toast("Full screen isn't available here", { description: "Try opening the page in its own browser tab." })
    );
  };

  return (
    <button
      type="button"
      data-testid="fullscreen-toggle-btn"
      data-state={on ? "on" : "off"}
      onClick={toggle}
      title={on ? "Exit full screen" : "Full screen"}
      aria-label={on ? "Exit full screen" : "Enter full screen"}
      className={`h-9 w-9 shrink-0 rounded-sm border flex items-center justify-center transition-[background-color,border-color,color,transform] duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-cyan-400 ${STYLES[variant]} ${className}`}
    >
      {on ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
    </button>
  );
};
